import { BUY_TOOL_NAME, INTAKE_TOOL_NAMES, PLAN_TOOL_NAMES } from '@blisspointmedia/bpm-types';
import { AgentResponseSegment } from '../../utils/agent-client';
import { PendingAgentOrder } from '../AgentOrderApproval/agent-order-approval-constants';
import { GUARDRAILS } from './governance-config';
import { applyLiveGuards, LiveBuyingPolicy } from './governance-live';
import { deriveOrchestrationTrace, TraceStatusType } from './orchestration-trace';
import { ROADMAP_AGENTS } from './roadmap-agents';

// Builds the orchestration WEB: a shared agent registry + audit log under which
// the planner and buying stages each run as a require-scoped, audited governed
// step, with human gates on every hand-off — each agent wired to the real tools /
// Newton blueprints / skill / guardrails it uses. The HERO is a single left→right
// governed flow — Newton pre-flight → Intake → Streaming Planner → Buying — with
// all four human gates rendered ON the flow line, in sequence, in the gaps between
// stages (G1 → G2 → G2.5 → G3), so the sequence reads unambiguously. The 2027-vision
// agents are summarized in one compact roadmap line (not a multi-row grid) to keep
// the flow dominant. Everything shown maps to a real inventory item (bpm-types
// GOVERNED_AGENTS + *_TOOL_NAMES, the guardrail policy, the Newton blueprints); the
// live agents' status reuses the exact derivation the linear trace trusts, so the
// graph and the trace never disagree. Deliberately NOT drawn as an autonomous
// "mastermind" dispatching four agents — the runtime is one governed conversational
// agent; intake is its front-door, and only the planner and buying stages dispatch
// through a governed step today.

export type WebNodeKind =
  'orchestrator' | 'agent' | 'tool' | 'blueprint' | 'skill' | 'guardrail' | 'gate';

export interface WebNode {
  id: string;
  kind: WebNodeKind;
  label: string;
  x: number;
  y: number;
  // live = actually executes today; false = 2027 roadmap / not-yet-live.
  live: boolean;
  // Present for the four governed agents (drives the node color).
  status?: TraceStatusType;
  // Owning governed-agent id for tools/blueprints/skill/guardrails (draws the spine).
  parent?: string;
  scope?: string;
  owner?: string;
  detail?: string;
  // For gate nodes: the y of the shared label band (all gate labels sit at one fixed
  // height ABOVE the flow line, so no gate label ever touches an agent box or chip).
  // The diamond stays on the flow line at `y`; the (two-line) label renders at labelY.
  labelY?: number;
  // For the buying guardrail chips whose value is displayed: true when the number
  // shown is live-resolved from the governance.policy event (the SAME source the
  // GovernancePanel uses), false when it is only a config default. Drives the
  // "· default" not-live treatment so a hardcoded number is never shown as live.
  valueIsLive?: boolean;
}

export type WebEdgeKind = 'coordinate' | 'handoff';

export interface WebEdge {
  id: string;
  from: string;
  to: string;
  kind: WebEdgeKind;
}

export interface OrchestrationWeb {
  nodes: WebNode[];
  edges: WebEdge[];
  width: number;
  height: number;
}

// A leaf attached to a governed agent (tool / blueprint / skill / guardrail).
type LeafSpec = Pick<WebNode, 'id' | 'label' | 'live' | 'kind' | 'valueIsLive'>;

// --- Layout constants (SVG user units; the panel scales via viewBox) ---
// The canvas is deliberately WIDE so the four-agent flow + four gates read as one
// clean left→right sequence. The planner→buying gap is widened to fit BOTH G2 and
// G2.5 on the line in order, and there is room to the right of buying for the
// terminal G3. WIDTH carries a right margin past G3's label. (This intentionally
// undoes the earlier ~1060 shrink: at that size the gates collided with the boxes.)
const WIDTH = 1440;
// Cap any single leaf column so it can't tower over the flow: the intake agent has
// 10 tools and the Newton column 9 blueprints. Over the cap we show the first 4
// then one synthetic overflow chip ("+N more"). Kept short (5) so the tool/guardrail
// columns stay visually SECONDARY to the flow. For buying this shows the 3 DURABLE
// guardrails (the governance headline) + a "+3 more" chip for the rollout controls.
// The true totals still live in LIVE_INVENTORY, so the inventory stays honest.
const MAX_LEAVES = 5;
const ORCH = { x: 685, y: 58 };
const AGENT_Y = 196; // the flow line — every agent box and every gate diamond sit on it
// Left→right in run order: Newton pre-flight → Intake → Planner → Buying. The
// Planner→Buying gap is the widest (buying pushed right) to seat G2 + G2.5 in order.
const AGENT_X: Record<string, number> = {
  'newton-preflight': 190,
  intake: 500,
  'streaming-planner': 810,
  buying: 1180
};
// All gate labels share ONE fixed band above the flow line (2-line, split on ' · ',
// centered over the diamond, white halo) so no label ever touches an agent box/chip.
const GATE_LABEL_BAND_Y = 112;
const LEAF_TOP = 264; // first tool/blueprint row under an agent (clear of the runtime lane)
const LEAF_STEP = 26; // vertical spacing between leaf nodes

// The four governed agents, in run order, with the human gate that guards entry
// to the NEXT stage. Gate copy mirrors core.py's Gate 1/2/2.5/3 wiring.
const AGENT_ORDER = ['newton-preflight', 'intake', 'streaming-planner', 'buying'] as const;

// Every gate diamond sits ON the flow line (y = AGENT_Y), in the gap it guards,
// placed by an explicit x so the sequence reads G1 → G2 → G2.5 → G3 left→right:
//   G1   in the Newton→Intake gap
//   G2   then G2.5 IN ORDER in the widened Planner→Buying gap
//   G3   on the line just AFTER Buying (the human-only commit)
const GATE_X: Record<string, number> = {
  'gate-1': 345, // midpoint of Newton(190)→Intake(500)
  'gate-2': 950, // Planner→Buying gap, first
  'gate-2_5': 1060, // Planner→Buying gap, second
  'gate-3': 1330 // just right of Buying(1180)
};

const GATES: { id: string; label: string }[] = [
  { id: 'gate-1', label: 'Gate 1 · confirm pre-filled intake' },
  { id: 'gate-2', label: 'Gate 2 · approve plan' },
  { id: 'gate-2_5', label: 'Gate 2.5 · authorize drafting' },
  { id: 'gate-3', label: 'Gate 3 · commit / send (human only)' }
];

// Frontend display overrides for the governed-agent box labels. The shared
// GOVERNED_AGENTS names (bpm-types) are unchanged; the demo just brands the
// pre-flight box as Bliss Point Audience & Planner (compact form to fit the node).
const AGENT_DISPLAY_LABELS: Record<string, string> = {
  'newton-preflight': 'Bliss Point A&P'
};

// Newton pre-flight blueprints (9 curated); the 4 brand-input ones run live today,
// the rest are wired but skipped without connector inputs (drawn as roadmap).
const BLUEPRINTS: { id: string; label: string; live: boolean }[] = [
  { id: 'bp-4cs', label: "4C's Analysis", live: true },
  { id: 'bp-market', label: 'Industry / Market Profile', live: true },
  { id: 'bp-segmentation', label: 'Audience Segmentation', live: true },
  { id: 'bp-scenario', label: 'Media Scenario Planning', live: true },
  { id: 'bp-competitive', label: 'Competitive Analysis', live: false },
  { id: 'bp-sizing', label: 'Audience Sizing', live: false },
  { id: 'bp-mri', label: 'MRI Audience Development', live: false },
  { id: 'bp-reach', label: 'Reach Curve Analysis', live: false },
  { id: 'bp-brief', label: 'Brief Interrogation', live: false }
];

const leafY = (index: number): number => LEAF_TOP + index * LEAF_STEP;

export interface OrchestrationWebInputs {
  segments: AgentResponseSegment[];
  newtonReplayed: boolean;
  orders: PendingAgentOrder[] | undefined;
  // The resolved durable buying policy from the live governance.policy event
  // (undefined until the planner run streams it). Passed through so the buying
  // guardrail chips show the SAME live-resolved numbers the GovernancePanel shows.
  livePolicy?: LiveBuyingPolicy;
}

export const buildOrchestrationWeb = ({
  segments,
  newtonReplayed,
  orders,
  livePolicy
}: OrchestrationWebInputs): OrchestrationWeb => {
  const trace = deriveOrchestrationTrace({ segments, newtonReplayed, orders });
  const byId = new Map(trace.map(stage => [stage.id, stage]));

  const nodes: WebNode[] = [];
  const edges: WebEdge[] = [];

  // Mastermind.
  nodes.push({
    id: 'orchestrator',
    kind: 'orchestrator',
    label: 'Orchestration Runtime',
    detail:
      'Shared agent registry + audit log. The planner and buying stages run as ' +
      'governed steps (identity require-scoped, action audited); human gates guard ' +
      'every hand-off.',
    x: ORCH.x,
    y: ORCH.y,
    live: true
  });

  // Governed agents + coordinate edges from the mastermind.
  AGENT_ORDER.forEach(id => {
    const stage = byId.get(id);
    if (!stage) return;
    const x = AGENT_X[id];
    nodes.push({
      id,
      kind: 'agent',
      label: AGENT_DISPLAY_LABELS[id] ?? stage.name,
      x,
      y: AGENT_Y,
      live: true,
      status: stage.status,
      scope: stage.scope,
      owner: stage.owner,
      detail: stage.detail
    });
    edges.push({ id: `coord-${id}`, from: 'orchestrator', to: id, kind: 'coordinate' });
  });

  // Sequential hand-offs.
  for (let i = 0; i < AGENT_ORDER.length - 1; i += 1) {
    edges.push({
      id: `handoff-${i}`,
      from: AGENT_ORDER[i],
      to: AGENT_ORDER[i + 1],
      kind: 'handoff'
    });
  }

  // Leaf clusters — the real tools / blueprints / skill / guardrails per agent.
  // Each leaf carries its own kind so guardrail/skill chips render distinctly from
  // plain tools; y is assigned by row order within the owning agent's cluster.
  const pushCluster = (parent: string, leaves: LeafSpec[]): void => {
    // Cap the column at MAX_LEAVES: show the first 6, then one overflow chip standing
    // in for the rest, so a long column (intake=10, Newton=9) can't tower over the
    // 7-row buying column. The true totals stay in LIVE_INVENTORY.
    const capped: LeafSpec[] =
      leaves.length > MAX_LEAVES
        ? [
            ...leaves.slice(0, MAX_LEAVES - 1),
            {
              id: `${parent}-more`,
              label: `+${leaves.length - (MAX_LEAVES - 1)} more`,
              live: true,
              kind: 'tool'
            }
          ]
        : leaves;
    capped.forEach((leaf, index) => {
      nodes.push({
        id: leaf.id,
        kind: leaf.kind,
        label: leaf.label,
        x: AGENT_X[parent],
        y: leafY(index),
        live: leaf.live,
        parent,
        valueIsLive: leaf.valueIsLive
      });
    });
  };

  const toolLeaf = (name: string): LeafSpec => ({
    id: `tool-${name}`,
    label: name,
    live: true,
    kind: 'tool'
  });

  pushCluster(
    'newton-preflight',
    BLUEPRINTS.map(bp => ({ id: bp.id, label: bp.label, live: bp.live, kind: 'blueprint' }))
  );
  pushCluster('intake', INTAKE_TOOL_NAMES.map(toolLeaf));
  pushCluster('streaming-planner', [
    ...PLAN_TOOL_NAMES.map(toolLeaf),
    { id: 'skill-rubric', label: 'skill · 41-criterion self-review', live: true, kind: 'skill' }
  ]);
  pushCluster('buying', [
    toolLeaf(BUY_TOOL_NAME),
    // Surface the numeric ceiling/cap on the web node itself (Harry's review: the
    // guardrail nodes read as labels with no values). Only the $-valued rows append a
    // value — the allow-list (a count) and the verbose send-lock states stay
    // label-only so the chip doesn't overflow (and carry no bare number that could
    // disagree with the panel). The $-valued ceilings live-bind to the SAME
    // governance.policy source the GovernancePanel uses (applyLiveGuards + livePolicy),
    // so the web and the panel can never show two different numbers for one guardrail.
    // Until the policy streams this session we render "· default" — never the
    // hardcoded number dressed up as a live fact.
    ...GUARDRAILS.map((g, i): LeafSpec => {
      if (!g.value.startsWith('$')) {
        return { id: `guardrail-${i}`, label: g.label, live: true, kind: 'guardrail' };
      }
      const resolved = applyLiveGuards(g, undefined, livePolicy);
      return {
        id: `guardrail-${i}`,
        label: resolved.isLive ? `${g.label} · ${resolved.value}` : `${g.label} · default`,
        live: true,
        kind: 'guardrail',
        valueIsLive: resolved.isLive
      };
    })
  ]);

  // Gates — every diamond sits ON the flow line (y = AGENT_Y), placed by its explicit
  // x so the four read as an in-order sequence in the gaps. Labels all share one fixed
  // band above the flow (labelY), never colliding with the agent boxes or the chips.
  GATES.forEach(gate => {
    nodes.push({
      id: gate.id,
      kind: 'gate',
      label: gate.label,
      x: GATE_X[gate.id],
      y: AGENT_Y,
      live: true,
      labelY: GATE_LABEL_BAND_Y
    });
  });

  // Height: extend just past the tallest leaf column (the 2027 roadmap is now a
  // compact summary line rendered by the panel, not a multi-row grid, so the canvas
  // no longer has to reserve room for it).
  const leafYs = nodes.filter(n => Boolean(n.parent)).map(n => n.y);
  const maxLeafY = leafYs.length > 0 ? Math.max(...leafYs) : AGENT_Y;
  const height = maxLeafY + 44;
  return { nodes, edges, width: WIDTH, height };
};

// Convenience for the panel/legend: counts of what actually runs today.
export const LIVE_INVENTORY = {
  agents: AGENT_ORDER.length,
  tools: INTAKE_TOOL_NAMES.length + PLAN_TOOL_NAMES.length + 1,
  blueprintsLive: BLUEPRINTS.filter(b => b.live).length,
  blueprintsTotal: BLUEPRINTS.length,
  gates: GATES.length,
  guardrails: GUARDRAILS.length,
  roadmapAgents: ROADMAP_AGENTS.length
} as const;

// The 2027 roadmap, collapsed to one compact summary (replaces the old multi-row
// grid of dimmed nodes — the biggest declutter). Keeps the honest live/roadmap
// boundary: the total count plus the Newton-provided agents named explicitly.
export const ROADMAP_SUMMARY = {
  total: ROADMAP_AGENTS.length,
  newtonNames: ROADMAP_AGENTS.filter(a => a.provider === 'newton').map(a => a.name)
} as const;
